import Spinner from "presentation/components/Shared/Spinner";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { confirmDialog } from "primereact/confirmdialog";
import { useFilterDistancyStore } from "presentation/stores/use-filter-distancy-store";
import { useDistancyCreateStore } from "presentation/stores/use-distancy-create-store";
import { useDistanceDeleteStore } from "presentation/stores/use-distancy-delete-store";
import { Distance } from "domain/entities/distance";
import { useDistanceByFilter } from "presentation/hooks/use-query-distancy";
import { DistanceInputFilter } from "domain/models/distance-input-filter";
import { DistanceModal } from "../DistanceModal";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import HeaderDistanceGrid from "./DistanceGridHeader";

interface DistanceGridProps {
  onSave: () => void;
  populateDistanceMutation: () => void;
  populateDistanceError: any;
  isPopulateDistanceError: boolean;
  isPopulateDistanceSuccess: boolean;
  distancePopulated: any;
  isPopulatePending: boolean;
}

const DistanceGrid: React.FC<DistanceGridProps> = ({ 
  onSave,
  populateDistanceMutation,
  populateDistanceError,
  isPopulateDistanceError,
  isPopulateDistanceSuccess,
  distancePopulated,
  isPopulatePending 
}) => {
  const { filters, setFilters } = useFilterDistancyStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "locationDestination",
    sortOrder: "asc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const { distanceType, setDistanceType } =
        useParameterStore();

  const {
    data: parameters,
    isLoading,
    isError,
    error,
    refetch,
  } = useDistanceByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as DistanceInputFilter);

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const { distance, setDistance } = useDistancyCreateStore();
  const { distanceToDelete, setDistanceToDelete } = useDistanceDeleteStore();
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    refetch();
  }, [lazyParams, refetch]);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 3000,
    });
  }

  const handleDelete = (rowData: Distance) => {
    setDistanceToDelete(rowData);
  };

  const showActionOptions = (rowData: Distance) => {
    return (
      <div className="spacing-icons">
        <i
          className="pi pi-trash cursor-pointer"
          onClick={() =>
            confirmAction("Deseja eliminar esta distância?", () =>
              handleDelete(rowData)
            )
          }
        />
        {"  "}
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => {
            setDistance(rowData);
            setIsModalVisible(true);
          }}
        />
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={parameters?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={parameters?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={[
          "locationOrigin",
          "locationDestination",
          "distanceKm",
          "type"
        ]}
        header={
          <HeaderDistanceGrid
            toast={toast}
            populateDistanceMutation={populateDistanceMutation}
            populateDistanceError={populateDistanceError}
            isPopulateDistanceError={isPopulateDistanceError}
            isPopulateDistanceSuccess={isPopulateDistanceSuccess}
            distancePopulated={distancePopulated}
            isPopulatePending={isPopulatePending}
          />}
        >
        <Column
          field="type"
          header="Tipo"
          filter
          sortable
          showFilterMatchModes={false}
        />
        <Column
          field="locationOrigin"
          header="Local de origem"
          filter
          sortable
          showFilterMatchModes={false}
        />
        <Column
          field="locationDestination"
          header="Local de destino"
          filter
          sortable
          showFilterMatchModes={false}
        />
        <Column
          field="distanceKm"
          header="Distância"
          sortable
          showFilterMatchModes={false}
        />
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
      <DistanceModal
        distanceType={distanceType}
        distance={distance}
        setDistance={setDistance}
        visible={isModalVisible}
        onHide={() => setIsModalVisible(false)}
        onSave={() => {
          setIsModalVisible(false);
          confirmAction(
            distance?.id
              ? "Deseja atualizar esta distância?"
              : "Deseja criar uma nova distância?",
            onSave
          );
        }}
      />
    </div>
  );
};

export default DistanceGrid;
